import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { getAdminId as resolveAdminId } from '../utils/getAdminId';
import { Customer } from '../types/pos';

interface CreateCustomerData {
  name: string;
  email?: string;
  phone?: string;
  address?: string;
  vat_number?: string;
  notes?: string;
}

export const useCustomers = () => {
  const { user } = useAuth();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getAdminId = useCallback(() => {
    if (!user) return null;
    return resolveAdminId(user);
  }, [user]);

  const loadCustomers = useCallback(async () => {
    if (!user) return;

    const adminId = getAdminId();
    if (!adminId) return;

    setLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase
        .from('customers')
        .select('*')
        .eq('admin_id', adminId)
        .order('name');

      if (fetchError) throw fetchError;
      setCustomers(data || []);
    } catch (err) {
      setError('Failed to load customers');
      console.error('Error loading customers:', err);
    } finally {
      setLoading(false);
    }
  }, [user, getAdminId]);

  useEffect(() => {
    if (user) {
      loadCustomers();
    }
  }, [user, loadCustomers]);

  const createCustomer = async (data: CreateCustomerData): Promise<Customer | null> => {
    if (!user) return null;

    const adminId = getAdminId();
    if (!adminId) return null;

    setLoading(true);
    setError(null);

    try {
      const { data: customer, error: insertError } = await supabase
        .from('customers')
        .insert({
          admin_id: adminId,
          name: data.name.trim(),
          email: data.email?.trim() || null,
          phone: data.phone?.trim() || null,
          address: data.address?.trim() || null,
          vat_number: data.vat_number?.trim() || null,
          notes: data.notes?.trim() || null
        })
        .select()
        .single();

      if (insertError) throw insertError;

      await loadCustomers();
      return customer;
    } catch (err) {
      setError('Failed to create customer');
      console.error('Error creating customer:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const updateCustomer = async (id: string, data: Partial<CreateCustomerData>): Promise<boolean> => {
    if (!user) return false;

    setLoading(true);
    setError(null);

    try {
      const updateData: Record<string, unknown> = { updated_at: new Date().toISOString() };

      if (data.name !== undefined) updateData.name = data.name.trim();
      if (data.email !== undefined) updateData.email = data.email?.trim() || null;
      if (data.phone !== undefined) updateData.phone = data.phone?.trim() || null;
      if (data.address !== undefined) updateData.address = data.address?.trim() || null;
      if (data.vat_number !== undefined) updateData.vat_number = data.vat_number?.trim() || null;
      if (data.notes !== undefined) updateData.notes = data.notes?.trim() || null;

      const { error: updateError } = await supabase
        .from('customers')
        .update(updateData)
        .eq('id', id);

      if (updateError) throw updateError;

      await loadCustomers();
      return true;
    } catch (err) {
      setError('Failed to update customer');
      console.error('Error updating customer:', err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const toggleFavorite = async (id: string): Promise<boolean> => {
    const customer = customers.find(c => c.id === id);
    if (!customer) return false;

    try {
      const { error: updateError } = await supabase
        .from('customers')
        .update({ is_favorite: !customer.is_favorite, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (updateError) throw updateError;

      setCustomers(prev => prev.map(c =>
        c.id === id ? { ...c, is_favorite: !c.is_favorite } : c
      ));
      return true;
    } catch (err) {
      console.error('Error toggling favorite:', err);
      return false;
    }
  };

  const deleteCustomer = async (id: string): Promise<boolean> => {
    if (!user) return false;

    setLoading(true);
    setError(null);

    try {
      const { error: deleteError } = await supabase
        .from('customers')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;

      await loadCustomers();
      return true;
    } catch (err) {
      setError('Failed to delete customer');
      console.error('Error deleting customer:', err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const searchCustomers = useCallback((query: string): Customer[] => {
    if (!query.trim()) return customers;

    const lowerQuery = query.toLowerCase();
    return customers.filter(c =>
      c.name.toLowerCase().includes(lowerQuery) ||
      c.email?.toLowerCase().includes(lowerQuery) ||
      c.phone?.includes(query)
    );
  }, [customers]);

  const getFavoriteCustomers = useCallback((): Customer[] => {
    return customers.filter(c => c.is_favorite);
  }, [customers]);

  const getRecentCustomers = useCallback((limit: number = 5): Customer[] => {
    return [...customers]
      .sort((a, b) => {
        const aDate = a.last_purchase_at ? new Date(a.last_purchase_at).getTime() : 0;
        const bDate = b.last_purchase_at ? new Date(b.last_purchase_at).getTime() : 0;
        return bDate - aDate;
      })
      .slice(0, limit);
  }, [customers]);

  const getTopCustomers = useCallback((limit: number = 5): Customer[] => {
    return [...customers]
      .sort((a, b) => b.total_purchases - a.total_purchases)
      .slice(0, limit);
  }, [customers]);

  return {
    customers,
    loading,
    error,
    createCustomer,
    updateCustomer,
    toggleFavorite,
    deleteCustomer,
    searchCustomers,
    getFavoriteCustomers,
    getRecentCustomers,
    getTopCustomers,
    refreshCustomers: loadCustomers
  };
};
